// @file The store for handling the surface onboarding demo padlet panel copy
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import {
  StepStatus,
  useSurfaceOnboardingDemoPadletPanelStore,
} from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import { defineStore } from 'pinia'
import { computed } from 'vue'

export enum OnboardingType {
  General = 'general',
  Teacher = 'teacher',
}

interface LandingPanelStep {
  text: string
}

type StepPanelContent = {
  [key in StepStatus]: {
    heading: string
    subheading?: string
    showButton: boolean
  }
}

export const useSurfaceOnboardingDemoPadletPanelCopyStore = defineStore(
  'surfaceOnboardingDemoPadletPanelCopyStore',
  () => {
    const surfaceStartingStateStore = useSurfaceStartingStateStore()
    const surfaceDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()
    const isTeacher = computed<boolean>(() => {
      return surfaceStartingStateStore.startingState.onboardingFlow?.isTeacher ?? false
    })
    const userOnboardingType = computed<OnboardingType>(() => {
      return isTeacher.value && isAppUsing('onboardingDemoV2') ? OnboardingType.Teacher : OnboardingType.General
    })

    // ------------------------------
    // Landing step
    // ------------------------------

    // TODO: ENG-19270 rename this getter to landingPanelContent
    const landingPanelSteps = computed((): LandingPanelStep[] => getLandingPanelSteps())
    function getLandingPanelSteps(): LandingPanelStep[] {
      if (!isAppUsing('onboardingDemoV2')) {
        return [{ text: __('Set a topic') }, { text: __('Add some posts') }, { text: __('Share and collaborate') }]
      }
      switch (userOnboardingType.value) {
        case OnboardingType.Teacher:
          return [
            { text: __('Set a topic for your lesson activity') },
            { text: __('Add some posts') },
            { text: __('Share and collaborate') },
          ]
        case OnboardingType.General:
          return [{ text: __('Set a topic') }, { text: __('Add some posts') }, { text: __('Share and collaborate') }]
        default:
          return [{ text: __('Set a topic') }, { text: __('Add some posts') }, { text: __('Share and collaborate') }]
      }
    }

    // ------------------------------
    // Topic step
    // ------------------------------

    const currentTopicPanelCopy = computed(() => {
      const allTopicPanelCopy = getTopicPanelCopy()
      return allTopicPanelCopy[surfaceDemoPadletPanelStore.activeStepStatus]
    })

    function getTopicPanelCopy(): StepPanelContent {
      switch (userOnboardingType.value) {
        case OnboardingType.Teacher:
          return {
            [StepStatus.NotStarted]: {
              heading: __(
                'Teachers use Padlet for all kinds of activities. To start, let’s create a discussion board!',
              ),
              subheading: __('Start by clicking the “Next” button below.'),
              showButton: true,
            },
            [StepStatus.InProgress]: {
              heading: __('For this example, we’ll create a gratitude board to share with your class.'),
              subheading: __('Time to add some posts to your new padlet!'),
              showButton: true,
            },
            [StepStatus.Complete]: {
              heading: __('We added a title and some sections to get you started.'),
              subheading: __('Time to add some posts to your new padlet!'),
              showButton: true,
            },
          }

        case OnboardingType.General:
          return {
            [StepStatus.NotStarted]: {
              heading: __('Let’s create a padlet about places around the world!'),
              subheading: __('Start by clicking the "Next" button below.'),
              showButton: true,
            },
            [StepStatus.InProgress]: {
              heading: __('Just a moment, your padlet is coming to life...'),
              showButton: true,
            },
            [StepStatus.Complete]: {
              heading: __('We added a title and some sections to get you started.'),
              subheading: __('Time to add some posts to your new padlet!'),
              showButton: true,
            },
          }

        default:
          return {
            [StepStatus.NotStarted]: {
              heading: __("Let's create a padlet about places around the world!"),
              subheading: __('Start by clicking the "Next" button below.'),
              showButton: true,
            },
            [StepStatus.InProgress]: {
              heading: __('Just a moment, your padlet is coming to life...'),
              showButton: true,
            },
            [StepStatus.Complete]: {
              heading: __('We added a title and some sections to get you started.'),
              subheading: __('Time to add some posts to your new padlet!'),
              showButton: true,
            },
          }
      }
    }

    // ------------------------------
    // Post step
    // ------------------------------

    const currentPostPanelCopy = computed(() => {
      const allPostPanelCopy = getPostPanelCopy()
      return allPostPanelCopy[surfaceDemoPadletPanelStore.activeStepStatus]
    })

    function getPostPanelCopy(): StepPanelContent {
      switch (userOnboardingType.value) {
        case OnboardingType.Teacher:
          return {
            [StepStatus.NotStarted]: {
              heading: __("Let's add your first post!"),
              subheading: __('Click the “Next” button to add an example post for your students to follow.'),
              showButton: true,
            },
            [StepStatus.InProgress]: {
              heading: __('Here’s an example for you!'),
              subheading: __('Tweak it to your liking and hit “Publish” to add this post to your padlet.'),
              showButton: true,
            },
            [StepStatus.Complete]: {
              heading: __('Awesome work!'),
              subheading: __('Now, let’s share your padlet to fill it with more posts!'),
              showButton: true,
            },
          }
        case OnboardingType.General:
          return {
            [StepStatus.NotStarted]: {
              heading: __('Let’s add your first post!'),
              subheading: __(
                'Click the "Next" button to add your thoughts, images, or links and watch your ideas take flight.',
              ),
              showButton: true,
            },
            [StepStatus.InProgress]: {
              heading: __('Here’s an idea for your first post!'),
              subheading: __('Tweak it to your liking and hit "Publish" to add this post to your padlet.'),
              showButton: true,
            },
            [StepStatus.Complete]: {
              heading: __('Awesome work!'),
              subheading: __('Let’s share your padlet to fill it with more posts!'),
              showButton: true,
            },
          }
        default:
          return {
            [StepStatus.NotStarted]: {
              heading: __('Let’s add your first post!'),
              subheading: __(
                'Click the "Next" button to add your thoughts, images, or links and watch your ideas take flight.',
              ),
              showButton: true,
            },
            [StepStatus.InProgress]: {
              heading: __('Here’s an idea for your first post!'),
              subheading: __('Tweak it to your liking and hit "Publish" to add this post to your padlet.'),
              showButton: true,
            },
            [StepStatus.Complete]: {
              heading: __('Awesome work!'),
              subheading: __('Let’s share your padlet to fill it with more posts!'),
              showButton: true,
            },
          }
      }
    }

    // ------------------------------
    // Share step
    // ------------------------------

    const currentSharePanelCopy = computed(() => {
      const allSharePanelCopy = getSharePanelCopy()
      return allSharePanelCopy[surfaceDemoPadletPanelStore.activeStepStatus]
    })

    function getSharePanelCopy(): StepPanelContent {
      switch (userOnboardingType.value) {
        case OnboardingType.Teacher:
          return {
            [StepStatus.NotStarted]: {
              heading: __(
                'For this first go, we’ll act as your students so you can see what real-time collaboration on Padlet is like!',
              ),
              showButton: true,
            },
            [StepStatus.InProgress]: {
              heading: '',
              showButton: true,
            },
            [StepStatus.Complete]: {
              heading: __('Check it out! Your students have discovered your padlet and added their own touches.'),
              showButton: true,
            },
          }
        case OnboardingType.General:
          return {
            [StepStatus.NotStarted]: {
              heading: __(
                'For this first go, we’ll act as your friends so you can see what real-time collaboration on Padlet is like!',
              ),
              showButton: true,
            },
            [StepStatus.InProgress]: {
              heading: '',
              showButton: true,
            },
            [StepStatus.Complete]: {
              heading: __('Check it out! New friends have discovered your padlet and added their own touches.'),
              showButton: true,
            },
          }
        default:
          return {
            [StepStatus.NotStarted]: {
              heading: __(
                'For this first go, we’ll act as your friends so you can see what real-time collaboration on Padlet is like!',
              ),
              showButton: true,
            },
            [StepStatus.InProgress]: {
              heading: '',
              showButton: true,
            },
            [StepStatus.Complete]: {
              heading: __('Check it out! New friends have discovered your padlet and added their own touches.'),
              showButton: true,
            },
          }
      }
    }

    // ------------------------------
    // Section names based on onboarding type
    // ------------------------------
    const sectionNames = computed(() => {
      switch (userOnboardingType.value) {
        case OnboardingType.Teacher:
          return [__('School & Learning'), __('Family & Friends'), __('Activities & Hobbies')]
        case OnboardingType.General:
          return [__('Favorite places'), __('Wishlist destinations'), __('No-go zones')]
        default:
          return [__('Favorite places'), __('Wishlist destinations'), __('No-go zones')]
      }
    })

    return {
      // ------------------------------
      // Landing step
      // ------------------------------
      landingPanelSteps,
      // ------------------------------
      // Topic step
      // ------------------------------
      currentTopicPanelCopy,
      // ------------------------------
      // Post step
      // ------------------------------
      currentPostPanelCopy,
      // ------------------------------
      // Share step
      // ------------------------------
      currentSharePanelCopy,
      // ------------------------------
      // Section names
      // ------------------------------
      sectionNames,
    }
  },
)
